import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiError, fetchActivity } from '@srichaitra/api';
import { errorMessageFor } from '@srichaitra/constants';

/**
 * The activity feed the trend charts are drawn from.
 *
 * Held raw. Every level of the drill-down is derived from it by the pure
 * functions in trends.js, which means a click never costs a request and the
 * chart, its breakdown and its transaction list can never disagree about the
 * same month.
 *
 * `forbidden` is tracked separately from `error` because it is not a failure:
 * the feed needs the History tile, and a role without it should simply not see
 * the trends section, rather than see an error telling it something broke.
 */

const initialState = {
  status: 'idle',
  entries: [],
  forbidden: false,
  error: null
};

function asRejection(error) {
  if (error instanceof ApiError) return { code: error.code, message: error.userMessage };
  return { code: 'UNKNOWN_ERROR', message: errorMessageFor('UNKNOWN_ERROR') };
}

export const loadActivity = createAsyncThunk('trends/load', async (_, { rejectWithValue }) => {
  try {
    return await fetchActivity();
  } catch (error) {
    if (error instanceof ApiError && (error.code === 'FORBIDDEN' || error.status === 403)) {
      return rejectWithValue({ forbidden: true });
    }
    return rejectWithValue(asRejection(error));
  }
});

const trendsSlice = createSlice({
  name: 'trends',
  initialState,
  reducers: {
    clearTrends() {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadActivity.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loadActivity.fulfilled, (state, action) => {
        state.status = 'ready';
        state.entries = action.payload ?? [];
        state.forbidden = false;
      })
      .addCase(loadActivity.rejected, (state, action) => {
        if (action.payload?.forbidden) {
          state.status = 'ready';
          state.entries = [];
          state.forbidden = true;
          return;
        }
        state.status = 'failed';
        state.error = action.payload ?? asRejection(null);
      });
  }
});

export const { clearTrends } = trendsSlice.actions;
export const trendsReducer = trendsSlice.reducer;

export const selectTrendsStatus = (state) => state.trends.status;
export const selectTrendsEntries = (state) => state.trends.entries;
export const selectTrendsError = (state) => state.trends.error;
export const selectTrendsForbidden = (state) => state.trends.forbidden;
